/**
 *  @file   Client facing module that implements Compute Groups API
 *  @module dcp/compute-groups
 *  @access public
 *  @author Kayra E-A <kayra@kingsds.network>
 *          Wes Garland <wes@kingsds.network>
 *          Paul <paul@kingsds.network>
 *  @date   Sept 2020
 *          February 2022
 *          May 2022
 */
'use strict';

const { getTransientConnection }  = require('dcp/utils/transient-connections');
const hash         = require('../../common/hash');
const { DCPError } = require('../../common/dcp-error');
const debugging    = require('dcp/debugging').scope('scheduler');
const { Address }  = require('dcp/dcp-client/wallet');
const constants = require('dcp/common/scheduler-constants');
const { clientError, reconstructServiceError } = require('dcp/utils');
const { BigNumber } = require('dcp/types');

/** @typedef {import('dcp/utils').apiServiceType} apiServiceType */
/** @typedef {import('dcp/utils').apiClientType} apiClientType */
/** @typedef {string} opaqueId */

/**
 * @typedef {object} cgAccessType
 * @property {opaqueId} [id]
 * @property {string} [joinKey]
 */

/**
 * @typedef {object} cgClientJoinType
 * @property {opaqueId} [id]
 * @property {Address} [joinAddress]
 * @property {string} [joinKey]
 * @property {string} [joinSecret]
 * @property {string} [joinHash]
 */

/**
 * @typedef {object} cgServiceJoinType
 * @property {opaqueId} [id]
 * @property {Address} [joinAddress]
 * @property {string} [joinKey]
 * @property {string} [joinHashHash]
 */

var explicitConnection;
Object.defineProperty(exports, 'serviceConnection', {
  get: () => { 
    if (explicitConnection)
      return explicitConnection;
    return getTransientConnection(dcpConfig.scheduler.services.computeGroups, 10);
  },
  set: (conn) => {explicitConnection = conn},
});

/**
 * (Used in jobs/index.js)
 * KeepAlive for the service connection to compute groups.
 */
exports.keepAlive = function keepAlive() {
  return exports.serviceConnection.keepalive().catch(err => console.error('Warning: keepalive failed for compute groups service', err));
}

/**
 *  Checks whether descriptor corresponds to the public compute group
 *    defined in scheduler constants.
 */
exports.isPublicComputeGroup = function isPublicComputeGroup(descriptor) {
  const publicGroup = constants.computeGroups.public;
  return descriptor.id === publicGroup.id
    || descriptor.opaqueId === publicGroup.opaqueId
    || descriptor.joinKey === publicGroup.joinKey;
};

/**
 * Returns a compute group identification snippet for diagnostic messages,
 * @param {object} descriptor - Must have one of the properties joinKey, id (id:=opaqueId). Specifically
 *                              descriptor = { joinKey: 'dcpDemo' } or descriptor = { id: 'bYcYGQ3NOpFnP4FKs6IBQd' },
 *                              where the corresponding row in table computeGroups have attributes
 *                              joinKey:='dcpDemo' or opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd' .
 * @returns {string}
 */
function cgId(descriptor) {
  return (descriptor.joinKey) ? `joinKey ${descriptor.joinKey}` : `id ${descriptor.id}`;
}

/**
 * Verify sufficient information in descriptor to access a compute group.
 * Emit diagnostics about unnecessary information.
 * @param {cgAccessType} descriptor 
 * @param {string} methodName 
 */
function validateCGDescriptor(descriptor, methodName) {
  for (const prop in descriptor) {
    if ([ 'id', 'joinKey' ].includes(prop)) continue;
    if ([ 'joinAddress', 'joinHash', 'joinSecret' ].includes(prop))
      console.warn(`It is not necessary to specify '${prop}' in the descriptor ${JSON.stringify(descriptor)} when calling ${methodName}`);
    else
      console.error(`Do not specify '${prop}' in the descriptor ${JSON.stringify(descriptor)} when calling ${methodName}`);
  }
}

/**
 * Verify sufficient information in descriptor to authorize a compute group.
 * Emit diagnostics about unnecessary information.
 * @param {cgClientJoinType} joinDescriptor 
 * @param {string} methodName 
 */
function validateCGJoinDescriptor(joinDescriptor, methodName) {
  for (const prop in joinDescriptor) {
    if ([ 'id', 'joinKey', 'joinSecret', 'joinHash', 'joinAddress' ].includes(prop)) continue;
    console.error(`Do not specify '${prop}' in the descriptor ${JSON.stringify(joinDescriptor)} when calling ${methodName}`);
  }
}

/**
 * Build message to go across the wire.
 * Verify sufficient information in descriptor to access a compute group.
 * Emit diagnostics about unnecessary information.
 * @param {cgAccessType} descriptor
 * @param {string} methodName
 * @returns {cgAccessType}
 */
function buildCGMessage(descriptor, methodName)
{
  if (exports.isPublicComputeGroup(descriptor)) return descriptor;

  const message = {};
  // Construct message.joinKey xor message.id .
  if (descriptor.joinKey) message.joinKey = descriptor.joinKey;
  else if (descriptor.id) message.id = descriptor.id; // id:=opaqueId

  debugging('computeGroups') && console.debug(`${methodName}:buildCGMessage: descriptor`, descriptor, 'message', message);

  validateCGDescriptor(descriptor, methodName);

  return message;
}

/**
 * Build message so that joinHash, joinSecret, opaqueId do not go across the wire.
 * Verify sufficient information in descriptor to authorize a compute group.
 * Emit diagnostics about unnecessary information.
 * @param {cgClientJoinType} descriptor
 * @param {string} methodName
 * @returns {cgServiceJoinType}
 */
function buildCGJoinMessage(descriptor, methodName)
{
  if (exports.isPublicComputeGroup(descriptor)) return descriptor;

  const message = {};
  // Construct message.joinKey xor message.id .
  if (descriptor.joinKey) message.joinKey = descriptor.joinKey;
  else if (descriptor.id) message.id = descriptor.id; // id:=opaqueId
  // Construct message.joinAddress .
  if (descriptor.joinAddress) message.joinAddress = descriptor.joinAddress;

  debugging('computeGroups') && console.debug(`${methodName}:buildCGJoinMessage: descriptor`, descriptor, 'message', message);

  validateCGJoinDescriptor(descriptor, methodName);

  // Construct message.joinHashHash .
  if (descriptor.joinSecret) message.joinHashHash = hash.calculate(hash.eh1, exports.calculateJoinHash(descriptor), exports.serviceConnection.dcpsid);
  if (descriptor.joinHash)   message.joinHashHash = hash.calculate(hash.eh1, descriptor.joinHash,                   exports.serviceConnection.dcpsid);

  return message;
}

function hasSufficientJoinInfo(joinDescriptor) {
  // Verify joinDescriptor has sufficient information to authorize a compute group (not guarenteed).
  return (joinDescriptor.joinKey && (joinDescriptor.joinSecret || joinDescriptor.joinHash))
    || (joinDescriptor.id && joinDescriptor.joinAddress)
    || exports.isPublicComputeGroup(joinDescriptor);
}

const newCGPrototype = { type: 'object',
  parameters: {
    // name:              { type: 'string', default: undefined },    /* name of group (length <= 255) */
    // description:       { type: 'string', default: undefined },    /* description of group (length <= 255) */
    // id:                { type: 'string', default: undefined },    /* opaqueId, the unique identifier of the compute group; nanoid (length === 22) */
    // joinKey:           { type: 'string', default: undefined },    /* basically the login (length <= 255) */
    // joinSecret:        { type: 'string', default: undefined },    /* basically the password (length <= 255) */
    // joinHash:          { type: 'string', default: undefined },    /* basically the password, the joinSecret seeded & hashed */
    // joinAddress:       { type: Address, default: undefined },     /* signature gives alternative to login/password */

    commissionRate:    { type: BigNumber, default: undefined }, /* commission, see DCP-1889 */
    deployFee:         { type: BigNumber, default: undefined }, /* number of DCC to take for every deployment */
    deployAccess:      { type: 'string', default: undefined },    /* can be "owner"|"join" (dcp-1910) */
    addJobFee:         { type: BigNumber, default: undefined }, /* fee required each time a job joins a compute group */
    maxTotalPayment:   { type: BigNumber, default: undefined }, /* limit on maximum job payment, NULL => Infinity */

    /* Administrative limits on group. NULL => Infinity: Should all be integers or undefined. */
    maxConcurrentJobs:      { type: 'number', default: undefined },
    maxConcurrentWorkers:   { type: 'number', default: undefined },
    maxConcurrentSandboxes: { type: 'number', default: undefined },
    maxConcurrentCPUs:      { type: 'number', default: undefined },
    maxConcurrentGPUs:      { type: 'number', default: undefined },
    maxConcurrentEscrow:    { type: BigNumber, default: undefined },
  },
};

/**
 * Async function that creates a new Compute Group.
 *
 * The joinDescriptor is of the form { joinKey, joinSecret }, { joinKey, joinHash } or { id, joinAddress }.
 * where id will correspond to the attribute opaqueId in the new row in the computeGroups table.
 *
 * This function can only be called with permission (which no longer exists /wg aug 2024)
 * Properties not appearing in newCGPrototype.parameters are not allowed in otherProperties.
 *
 * @param {cgClientJoinType} joinDescriptor - Must have properly defined { joinKey, joinSecret }, { joinKey, joinHash }
 *                                            or { id, joinAddress }, where id will correspond to the attribute opaqueId
 *                                            in the new row in the computeGroups table.
 * @param {string} [name]                   - The name of the compute group.
 * @param {string} [description]            - The description of the compute group.
 * @param {object} [otherProperties]        - The 5 attributes of table computeGroup related to commissions and fees.
 *                                      commissionRate:     notNull(zFinNum),// commission, see DCP-1889
 *                                      deployFee:          notNull(zFinNum),// number of DCC to take for every deployment
 *                                      deployAccess:       string,          // can be "owner"|"join" (dcp-1910)
 *                                      addJobFee:          notNull(zFinNum),// fee required each time a job joins a compute group
 *                                      maxTotalPayment:    finNum,          // limit on maximum job payment, NULL => Infinity
 *                                     And the 6 attributes of table computeGroup related to limits.
 *                                      maxConcurrentJobs:      integer,
 *                                      maxConcurrentWorkers:   integer,
 *                                      maxConcurrentSandboxes: integer,
 *                                      maxConcurrentCPUs:      integer,
 *                                      maxConcurrentGPUs:      integer,
 *                                      maxConcurrentEscrow:    finNum,
 * @returns {Promise<apiClientType>}   - { success, payload: computeGroup.id }
 * @access public
 * @example
 * await computeGroup.createGroup({ joinKey: 'dcpDemo', joinSecret: 'theSecret' }, 'myCGName', 'myCGDescription', { deployFee: 0.00015 });
 * await computeGroup.createGroup({ joinKey: 'dcpDemo2', joinHash: 'eh1-e063976b20a92da97a27b9873465c6f2c9d6e4370befa86c8c1dd312c78befc2' });
 * await computeGroup.createGroup({ id: 'bYcYGQ3NOpFnP4FKs6IBQd', joinAddress: 'c15053fc30d4bdf91e2e0bba79578f8b649e55ea' });
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo',
 *    row2: joinKey:='dcpDemo2', joinHash:='eh1-e063976b20a92da97a27b9873465c6f2c9d6e4370befa86c8c1dd312c78befc2'
 *    row3: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd', joinAddress:='c15053fc30d4bdf91e2e0bba79578f8b649e55ea' .
 */
exports.createGroup = async function createGroup(joinDescriptor, name, description, otherProperties)
{
  validateCGJoinDescriptor(joinDescriptor, 'createGroup');

  // Verify joinDescriptor has sufficient information to authorize a compute group (not guarenteed).
  if (!hasSufficientJoinInfo(joinDescriptor))
    return clientError(`createGroup: Insufficient information to authorize compute group: ${JSON.stringify(joinDescriptor)}.`);

  // Validate the properties in otherProperties.
  for (const methodName in otherProperties) {
    if (!Object.keys(newCGPrototype.parameters).includes(methodName))
      return clientError(`createGroup: Property ${methodName} cannot be speicfied in otherProperties. Can only specify ${JSON.stringify(Object.keys(newCGPrototype.parameters))}`);
  }

  // Translate joinSecret to joinHash.
  if (joinDescriptor.joinSecret) {
    joinDescriptor.joinHash = exports.calculateJoinHash(joinDescriptor);
    delete joinDescriptor.joinSecret;
  }

  if (otherProperties && (otherProperties.commissionRate < 0 || otherProperties.commissionRate >= 1))
    return clientError(`client-createGroup: commissionRate ${otherProperties.commissionRate} must be between 0 and 1 (0 <= commissionRate < 1).`);

  debugging('computeGroups') && console.debug('client-createGroup: input:', joinDescriptor, name, description, otherProperties);

  try
  {
    const { success, payload } = await exports.serviceConnection.request('createGroup', { joinDescriptor, name, description, otherProperties });

    debugging('computeGroups') && console.debug('client-createGroup: payload', payload);

    if (!success)
      return clientError(`Cannot create new compute group, with ${cgId(joinDescriptor)}.`);
    if (payload && !payload.success)
      return reconstructServiceError(payload);
    
    return payload;
  }    
  catch(error)
  {
    debugging('computeGroups') && console.debug('Error creating compute group:', error);
    return clientError('Error creating compute group: ' + error.message);    
  }
};

const changeCGPrototype = { type: 'object',
  parameters: {
    name:              { type: 'string', default: undefined },    /* name of group (length <= 255) */
    description:       { type: 'string', default: undefined },    /* description of group (length <= 255) */
    joinHash:          { type: 'string', default: undefined },    /* basically the password, seeded & hashed (length <= 255) */
    joinAddress:       { type: Address, default: undefined },     /* signature gives alternative to login/password */

    commissionRate:    { type: BigNumber, default: undefined }, /* commission, see DCP-1889 */
    deployFee:         { type: BigNumber, default: undefined }, /* number of DCC to take for every deployment */
    deployAccess:      { type: 'string', default: undefined },    /* can be "owner"|"join" (dcp-1910) */
    addJobFee:         { type: BigNumber, default: undefined }, /* fee required each time a job joins a compute group */
    maxTotalPayment:   { type: BigNumber, default: undefined }, /* limit on maximum job payment, NULL => Infinity */

    /* Administrative limits on group. NULL => Infinity: Should all be integers or undefined. */
    maxConcurrentJobs:      { type: 'number', default: undefined },
    maxConcurrentWorkers:   { type: 'number', default: undefined },
    maxConcurrentSandboxes: { type: 'number', default: undefined },
    maxConcurrentCPUs:      { type: 'number', default: undefined },
    maxConcurrentGPUs:      { type: 'number', default: undefined },
    maxConcurrentEscrow:    { type: BigNumber, default: undefined },
  },
};

/**
 * Async function that changes a new Compute Group.
 * 
 * The parameter newDescriptor contains the new property values,
 * and the properties that are allowed to be changed appear in changeCGPrototype.parameters.
 * 
 * The descriptor must have joinKey or id, where id:=opaqueId.
 * Must own the compute group to use changeGroup.
 * 
 * @param {cgAccessType} descriptor - Must have joinkey or id, where id:=opaqueId.
 * @param {object} newDescriptor    - Properties not appearing in changeCGPrototype.parameters are not allowed.
 * @returns {Promise<apiClientType>}
 * await computeGroup.changeGroup({ joinKey: 'dcpDemo' }, { joinSecret: 'myNewPasswrd' });
 * await computeGroup.changeGroup({ id: 'bYcYGQ3NOpFnP4FKs6IBQd' }, { name: 'myNewName', deployFee: 0.0001 });
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo',
 *    row2: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd'
 *
 * @returns {Promise} which rejects with an instance of Error when we can't talk to the compute groups
 *                    daemon or change the group.
 */
exports.changeGroup = async function changeGroup(descriptor, newDescriptor)
{
  // Verify descriptor has sufficient information to access a compute group (not guarenteed).
  if (!descriptor.joinKey && !descriptor.id)
    return clientError(`changeGroup: Insufficient information to identify compute group: ${JSON.stringify(descriptor)}.`);

  // Validate the properties in newDescriptor.
  for (const methodName in newDescriptor) {
    if (!Object.keys(changeCGPrototype.parameters).includes(methodName))
      return clientError(`changeGroup: Not allowed to change property ${methodName}. Can only change ${JSON.stringify(Object.keys(changeCGPrototype.parameters))}`);
  }

  // Translate joinSecret to joinHash.
  if (newDescriptor.joinSecret) {
    newDescriptor.joinHash = exports.calculateJoinHash(newDescriptor);
    delete newDescriptor.joinSecret;
  }

  descriptor = buildCGMessage(descriptor, 'changeGroup');
  debugging('computeGroups') && console.debug('change compute group client:', descriptor, newDescriptor);

  const { success, payload } = await exports.serviceConnection.request('changeGroup', { descriptor, newDescriptor });

  if (!success) throw new DCPError(`Cannot change compute group with ${cgId(descriptor)}:`, payload);
  if (payload && !payload.success) return reconstructServiceError(payload);

  return payload;
};

/**
 * Async function that deletes a compute group.
 * 
 * The descriptor must have joinkey or id, where id:=opaqueId.
 * 
 * Must own the group. The following config must be true:
 *     dcpConfig.scheduler.services.computeGroups.usersCanDeleteGroups
 * 
 * @param {cgAccessType} descriptor - Must contain joinKey or id (id:=opaqueId) 
 * @returns {Promise<apiClientType>}
 * await computeGroup.deleteGroup({ joinKey: 'dcpDemo' });
 * await computeGroup.deleteGroup({ id: 'bYcYGQ3NOpFnP4FKs6IBQd' });
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo',
 *    row2: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd'
 *
 * @returns {Promise} which rejects with an instance of Error when we can't talk to the compute groups
 *                    daemon or delete the group.
 */
exports.deleteGroup = async function deleteGroup(descriptor)
{
  // Verify descriptor has sufficient information to access a compute group (not guarenteed).
  if (!descriptor.joinKey && !descriptor.id)
    return clientError(`deleteGroup: Insufficient information to identify compute group: ${JSON.stringify(descriptor)}.`);

  descriptor = buildCGMessage(descriptor, 'deleteGroup');
  debugging('computeGroups') && console.debug('delete compute group client:', descriptor);
  const { success, payload } = await exports.serviceConnection.request('deleteGroup', { descriptor });

  if (!success) throw new DCPError(`Cannot delete compute group with ${cgId(descriptor)}:`, payload);
  if (payload && !payload.success) return reconstructServiceError(payload);

  return payload;
};

/**
 * Async function that adds a job to a specified compute group. 
 * 
 * Must be the owner of the job.
 *
 * Useful feedback is provided from this function, as it
 * will make its way back to the application developer, *after* they have made the
 * deployment fee micropayment.
 *
 * On the client side the access model in place is that if you know the (user/password)
 * joinKey+joinSecret/joinKey+joinHash/joinKey+joinHashHash/id+joinAddress,
 * you can add the job to the compute groups, where id:=opaqueId from table computeGroups.
 * On the service side the corresponding access model is
 *   joinKey+joinHashHash/id+joinAddress .
 * Access is also allowed if the compute group owner is the connection peerAddress.
 * 
 * Unless the compute group owner is the connection peerAddress, element of the descriptor array must contain
 *   { joinKey, joinSecret }, { joinKey, joinHash } or { id, joinAddress }
 * where the value of id in { id, joinAddress } is the opaqueId attribute of the row in table computeGroups.
 *
 * @param {Address} job              The address of the Job that will be added to the Compute Group.
 * @param {Array} computeGroups      Array of descriptor objects for the compute groups. This descriptor
 *                                   needs to contain enough information to authorize access to the
 *                                   compute group. Properties may include:
 *                                      - id (id:=opaqueId)
 *                                      - joinKey
 *                                      - joinSecret
 *                                      - joinHash
 *                                      - joinAddress
 * 
 *                                   Additional, either the joinKey or id MUST be specified so
 *                                   that we can identify the compute group in question.
 *
 *                                   All compute groups can have jobs submitted to them, provided either the joinKey
 *                                   or the id are specified, and the message contains valid join permission and the 
 *                                   job is owned by the caller of addJobToGroups.
 *
 *                      FUTURE - after DCP-1910
 *                      keystore     A keystore used to grant access to job deployment within this compute group.
 *                                   This can be either the ownerKeystore or the joinAddress keystore when the
 *                                   compute group is in deployAccessType='join' mode.
 * @returns {Promise<apiClientType>}
 * @access public
 * @example
 * await computeGroup.addJobToGroups('P+Y4IApeFQLrYS2W7MkVg7', 
 *    [ { joinKey: 'dcpDemo', joinSecret: 'theSecret' },
 *      { joinKey: 'dcpDemo2', joinHash: 'eh1-e063976b20a92da97a27b9873465c6f2c9d6e4370befa86c8c1dd312c78befc2' }, 
 *      { id: 'bYcYGQ3NOpFnP4FKs6IBQd', joinAddress: 'c15053fc30d4bdf91e2e0bba79578f8b649e55ea' } ]);
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo',
 *    row2: joinKey:='dcpDemo2', joinHash:='eh1-e063976b20a92da97a27b9873465c6f2c9d6e4370befa86c8c1dd312c78befc2'
 *    row3: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd', joinAddress:='c15053fc30d4bdf91e2e0bba79578f8b649e55ea' .
 *
 * @returns {Promise} which rejects with an instance of Error when we can't talk to the compute groups
 *                    daemon or add the job to the group.
 */
exports.addJobToGroups = async function addJobToGroups(job, computeGroups)
{
  // ensure the service connection is hot before calling buildCGJoinMessage:
  await exports.keepAlive();

  const cgArray = [];
  for (const joinDescriptor of computeGroups)
  {
    // Verify joinDescriptor has sufficient information to authorize a compute group (not guarenteed).
    if (!hasSufficientJoinInfo(joinDescriptor))
    {
      console.error(`addJobToGroups: Insufficient information to authorize compute group: ${JSON.stringify(joinDescriptor)}.`);
      continue;
    }

    // Translate so that neither joinHash nor joinSecret goes across the wire.
    const message = buildCGJoinMessage(joinDescriptor, 'addJobToGroups');
    debugging('computeGroups') && console.debug(`addJobToGroups client: job ${job}, message`, message);

    cgArray.push(message);
  }

  const { success, payload } = await exports.serviceConnection.request('addJobToGroups', { job, cgArray });

  debugging('computeGroups') && console.debug('addJobToGroups payload', payload);

  if (!success) throw new DCPError(`Cannot add job ${job} to compute groups.`, payload);
  if (payload && !payload.success) return reconstructServiceError(payload);

  // If the server reported success but did not return a list of CGs (eg. v.4.2.5 server),
  // assume (and inform the client) we added all groups successfully
  return payload || computeGroups;
};

/**
 * Async function that lists all the Jobs in a Compute Group.
 * 
 * The descriptor must have one of the properties joinkey, id (id:=opaqueId).
 * Must be the owner of the Compute Group to list jobs from it.
 * The job does not need to be owned.
 * 
 * The descriptor is of the form { joinKey: 'dcpDemo' } or { id: 'bYcYGQ3NOpFnP4FKs6IBQd' }.
 * where 'bYcYGQ3NOpFnP4FKs6IBQd' is the opaqueId of the Compute Group.
 *
 * @param {cgAccessType} descriptor - Must have one of the properties joinKey, id (id:=opaqueId). Specifically
 *                                    descriptor = { joinKey: 'dcpDemo' } or descriptor = { id: opaqueId }
 * @returns {Promise<apiClientType>}
 * @access public
 * @example
 * let listOfJobs1 = await computeGroup.listJobs({ joinKey: 'dcpDemo' });
 * let listOfJobs2 = await computeGroup.listJobs({ id: 'bYcYGQ3NOpFnP4FKs6IBQd' });
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo'
 *    row2: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd'
 *
 * @returns {Promise} which rejects with an instance of Error when we can't talk to the compute groups
 *                    daemon or list jobs in the group.
 */
exports.listJobs = async function listJobs(descriptor)
{
  // Verify descriptor has sufficient information to access a compute group (not guarenteed).
  if (!descriptor.joinKey && !descriptor.id)
    return clientError(`listJobs: Insufficient information to identify compute group: ${JSON.stringify(descriptor)}.`);

  descriptor = buildCGMessage(descriptor, 'listJobs');
  debugging('computeGroups') && console.debug('listJob client: descriptor', descriptor);
  const { success, payload } = await exports.serviceConnection.request('listJobs', { descriptor });

  if (!success) throw new DCPError(`Cannot list jobs for compute group with ${cgId(descriptor)}`, payload);
  if (payload && !payload.success) return reconstructServiceError(payload);

  return payload;
};

/**
 * Async function that removes a job from a Compute Group.
 * 
 * The descriptor must have one of the properties joinkey, id (id:=opaqueId).
 * Must be the owner of the Compute Group to remove a job from it.
 * The job does not need to be owned.
 * 
 * The descriptor is of the form { joinKey: 'dcpDemo' } or { id: 'bYcYGQ3NOpFnP4FKs6IBQd' }.
 * where 'bYcYGQ3NOpFnP4FKs6IBQd' is the opaqueId of the Compute Group.
 *
 * @param {Address} job              - The address of the Job that will be added to the Compute Group.
 * @param {cgAccessType}  descriptor - { joinKey: 'dcpDemo' } or { id: 'bYcYGQ3NOpFnP4FKs6IBQd' }
 * @returns {Promise<apiClientType>}
 * @access public
 * @example
 * await computeGroup.removeJob( 'P+Y4IApeFQLrYS2W7MkVg7', { joinKey: 'dcpDemo' });
 * await computeGroup.removeJob( 'P+Y4IApeFQLrYS2W7MkVg7', { id: 'bYcYGQ3NOpFnP4FKs6IBQd' });
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo'
 *    row2: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd'
 *
 * @returns {Promise} which rejects with an instance of Error when we can't talk to the compute groups
 *                    daemon or remove the job from the group.
 */
exports.removeJob = async function removeJob(job, descriptor)
{
  // Verify descriptor has sufficient information to access a compute group (not guarenteed).
  if (!descriptor.joinKey && !descriptor.id)
    return clientError(`removeJob: Insufficient information to identify compute group: ${JSON.stringify(descriptor)}.`);

  descriptor = buildCGMessage(descriptor, 'removeJob');
  debugging('computeGroups') && console.debug(`removeJob client: job ${job}, descriptor`, descriptor);
  const { success, payload } = await exports.serviceConnection.request('removeJob', { job, descriptor });

  if (!success) throw new DCPError(`Cannot remove job ${job} from compute group with ${cgId(descriptor)}`, payload);
  if (payload && !payload.success) return reconstructServiceError(payload);

  return payload;
};

/**
 * Async function that removes all jobs from a Compute Group.
 * 
 * The descriptor must have one of the properties joinkey, id (id:=opaqueId).
 * Must be the owner of the Compute Group to remove jobs from it.
 * 
 * The descriptor is of the form { joinKey: 'dcpDemo' } or { id: 'bYcYGQ3NOpFnP4FKs6IBQd' }.
 * where 'bYcYGQ3NOpFnP4FKs6IBQd' is the opaqueId of the Compute Group.
 *
 * @param {cgAccessType} descriptor - { joinKey: 'dcpDemo' } or { id: 'bYcYGQ3NOpFnP4FKs6IBQd' }
 * @returns {Promise<apiClientType>}
 * @access public
 * @example
 * await computeGroup.removeAllJobs({ joinKey: 'dcpDemo' });
 * await computeGroup.removeAllJobs({ id: 'bYcYGQ3NOpFnP4FKs6IBQd' });
 * where the corresponding rows in table computeGroups have attributes
 *    row1: joinKey:='dcpDemo'
 *    row2: opaqueId:='bYcYGQ3NOpFnP4FKs6IBQd'
 *
 * @returns {Promise} which rejects with an instance of Error when we can't talk to the compute groups
 *                    daemon or remove all the jobs from the group.
 */
exports.removeAllJobs = async function removeAllJobs(descriptor)
{
  // Verify descriptor has sufficient information to access a compute group (not guarenteed).
  if (!descriptor.joinKey && !descriptor.id)
    return clientError(`removeAllJobs: Insufficient information to identify compute group: ${JSON.stringify(descriptor)}.`);

  descriptor = buildCGMessage(descriptor, 'removeAllJobs');
  debugging('computeGroups') && console.debug('removeAllJobs client: descriptor', descriptor);
  const { success, payload } = await exports.serviceConnection.request('removeAllJobs', { descriptor });

  if (!success) throw new DCPError(`Cannot remove all jobs from compute group with ${cgId(descriptor)}:`, payload);
  if (payload && !payload.success) return reconstructServiceError(payload);

  return payload;
};

Object.assign(exports, require('./authorization'));
